<template>
  <div class="wrapper" tabindex="-1">
    <div class="menu-header">
      <button v-if="currentFolder.parentIsFolder" @click="lookUp" class="back">
        <span class="sr-only">Go Back to Parent Folder</span>
      </button>
      {{currentFolder.name}}
      <button @click="close($event)" class="btn-icon grey-to-red close">
        <span class="icon times"></span>
        <span class="sr-only">Close the context menu</span>
      </button>
    </div>
    <transition :name="contentName">
      <div class="content" :key="currentFolder.id">
        <ul>
          <li v-for="childFolder in currentFolder.folders">
            <button :key="childFolder.id"
                    @click="lookDown(childFolder)"
                    class="folder"
                    :disabled="isFolderButtonDisabled(childFolder)">
              <icon class="folder-closed" :class="{inactive: isFolderButtonDisabled(childFolder)}"></icon> <span>{{childFolder.name}}</span>
            </button>
          </li>
          <li v-for="childDocument in currentFolder.organizationDocumentsBy('title')">
            <div :key="childDocument.id" :class="StringHelper.camelToDash(childDocument.classKey)">
              <icon class="inactive" :class="StringHelper.camelToDash(childDocument.classKey)"></icon> <span>{{childDocument.title}}</span>
            </div>
          </li>
        </ul>
      </div>
    </transition>

    <transition name="slide-right">
      <div v-if="showAddFolder" class="menu-footer" :key="'Add Folder'">
        <div class="folder-name">
          <input v-model="newFolderName" ref="newFolderName" @keyup.enter="addFolder" @keyup.stop.esc="resetAddFolder"
                 type="text"
                 placeholder="Enter folder name...">
          <button @click="addFolder" class="check" >
            <span class="sr-only">Confirm New Folder Name</span>
          </button>
          <button @click="resetAddFolder" class="btn-icon grey-to-red close">
            <span class="icon times"></span>
            <span class="sr-only">Cancel New Folder Name</span>
          </button>
        </div>
      </div>
    </transition>
    <transition name="slide-left">
      <div v-if="!showAddFolder" class="menu-footer" :key="'Move Here'">
        <template v-if="this.options.selectOnly">
          <button @click="selectFolder" class="btn xs">
            Select
          </button>
        </template>
        <template v-else>
          <button @click="revealAddFolder" class="add-folder" :disabled="!$can('createDocumentsAndFolders', this.currentFolder)">
            <span class="sr-only">Create New Folder</span>
          </button>
          <button @click="moveHere" class="btn xs" :disabled="!$can('moveArchive', this.currentFolder)">
            Move here
          </button>
        </template>
      </div>
    </transition>

  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import Folder from "../models/folder";
  import OrganizationDocument from "../models/organizationDocument";
  import ListView from "../../../common/models/listViews/listView";
  import {StringHelper} from "../../../common/helpers/stringHelper";
  import Mapping from "../../../common/models/mapping/mapping";
  import Organization from "../../../common/models/organization";
  import PublicProfile from "../../../common/models/publicProfile/publicProfile";
  import DetailView from "~/models/detailView/detailView";
  import ProcessFlow from "~/models/processFlows/processFlow";
  import SelectedItemMixin from "../mixins/selectedItemMixin"

  export default Vue.extend({
    mixins: [SelectedItemMixin],
    props: {
      resource: {
        type: Object,
      },
      folder: {
        type: Object as () => Folder,
        required: true,
      },
      options: {
        type: Object,
      },
      selectOnly: {
        type: Boolean,
        default: false,
      }
    },
    data: function () {
      return {
        newFolderName: '',
        showAddFolder: false,
        direction: '',
        StringHelper: StringHelper,
        currentFolder: this.folder,
      }
    },
    computed: {
      contentName() {
        return this.direction === 'up' ? 'shift-left' : 'shift-right'
      },
      organization(): Organization {
        return this.currentFolder.organization;
      },
    },
    methods: {
      lookUp() {
        if (this.currentFolder.parentIsFolder) {
          let folder = this.currentFolder.parent;
          this.direction = 'up';
          this.currentFolder = folder;
          if (!folder.hasDocuments()) {
            this.$store.dispatch(`${this.options.store}/fetchDocuments`, {folder: folder})
          }
        }
      },
      lookDown(folder) {
        if (this.options.selectOnly || this.resource.canMoveTo(folder)) {
          this.direction = 'down';
          this.currentFolder = folder;
          if (!folder.hasDocuments()) {
            this.$store.dispatch(`${this.options.store}/fetchDocuments`, {folder: folder})
          }
        }
      },
      moveHere() {
        let action = `${this.options.store}/move`;
        let data = {organization: this.organization, newParent: this.currentFolder};
        if (this.resource instanceof Folder) {
          action += 'Folder';
          data['folder'] = this.resource;
        } else if (this.resource instanceof OrganizationDocument || this.resource instanceof ListView || this.resource instanceof Mapping || this.resource instanceof PublicProfile || this.resource instanceof DetailView || this.resource instanceof ProcessFlow) {
            action += 'Document';
            data['document'] = this.resource;
        }

        this.$store.dispatch(action, data).then(() => {
          this.$emit('moveHere', {resource: this.resource});
          this.close(null);
        });
      },
      revealAddFolder() {
        this.showAddFolder = true;
        this.$nextTick(() => this.$refs.newFolderName.focus());
      },
      addFolder: function () {
        let newFolder = new Folder().loadFromJson({name: this.newFolderName, parent: this.currentFolder});
        this.$store.dispatch(
          `${this.options.store}/createFolder`,
            {organization: this.organization, folder: newFolder, application: this.selectedApplication}
        ).then((folder) => {
          this.resetAddFolder();
          this.lookDown(folder);
        });
      },
      resetAddFolder() {
        this.newFolderName = '';
        this.showAddFolder = false;
        this.focus();
      },
      focus() {
        this.$el.focus();
      },
      close(event) {
        this.$emit('close');
      },
      isFolderButtonDisabled(childFolder: Folder): boolean {
        if (this.options.selectOnly) {
          return false
        } else {
          return !this.resource.canMoveTo(childFolder) || !this.$can('moveArchiveChild', childFolder)
        }
      },
      selectFolder(): void {
        this.$emit('selected', this.currentFolder)
      },
    },
  });
</script>
